import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Download, Loader2, ChevronRight } from 'lucide-react';
import { newsletters } from '../data/blogPosts';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  const newsletter = newsletters.find((n) => n.slug === slug);

  // If not found by slug, redirect to listing
  if (!newsletter) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500 text-lg">Newsletter not found.</p>
        <Link to="/blog" className="text-blue-600 hover:text-blue-700 font-medium">
          Back to Insights
        </Link>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const { data, error } = await supabase.storage
        .from('newsletters')
        .createSignedUrl(newsletter.pdfFile, 300);

      if (error || !data?.signedUrl) {
        console.error('Failed to generate signed URL:', error);
        return;
      }

      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening newsletter PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  // Find prev/next for navigation
  const sorted = [...newsletters].sort(
    (a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
  );
  const currentIndex = sorted.findIndex((n) => n.slug === slug);
  const prevPost = currentIndex < sorted.length - 1 ? sorted[currentIndex + 1] : null;
  const nextPost = currentIndex > 0 ? sorted[currentIndex - 1] : null;

  return (
    <>
      <SEO
        title={`${newsletter.title} — Insights | The Fuel Central`}
        description={newsletter.excerpt}
        keywords="gas station industry insights, fuel retail newsletter, California gas station market, petroleum industry updates"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Link
                to="/blog"
                className="inline-flex items-center text-blue-200 hover:text-white transition-colors mb-6 text-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Insights
              </Link>
              <div className="flex items-center gap-2 text-blue-200 text-sm mb-3">
                <Calendar className="w-4 h-4" />
                {formatDate(newsletter.publishedDate)}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-3">{newsletter.title}</h1>
              {newsletter.subtitle && (
                <p className="text-blue-200 text-lg">{newsletter.subtitle}</p>
              )}
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="container mx-auto px-4 py-10">
          <div className="max-w-3xl mx-auto">
            {/* PDF Download Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <p className="text-sm text-blue-800">
                This article is also available as a PDF newsletter.
              </p>
              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors whitespace-nowrap"
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Opening...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download PDF
                  </>
                )}
              </button>
            </div>

            {/* Article Body */}
            <article
              className="prose prose-lg prose-slate max-w-none
                prose-headings:text-gray-900 prose-headings:font-bold
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed
                prose-li:text-gray-700
                prose-strong:text-gray-900
                prose-blockquote:border-blue-300 prose-blockquote:bg-blue-50 prose-blockquote:rounded-r-lg prose-blockquote:py-2 prose-blockquote:px-4
                prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: newsletter.content }}
            />

            {/* Prev/Next Navigation */}
            <div className="border-t border-gray-200 mt-12 pt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {prevPost ? (
                <Link
                  to={`/blog/${prevPost.slug}`}
                  className="group flex flex-col p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <span className="text-xs text-gray-500 mb-1">Previous</span>
                  <span className="text-sm font-medium text-gray-900 group-hover:text-blue-700 transition-colors">
                    {prevPost.title}
                  </span>
                </Link>
              ) : (
                <div />
              )}
              {nextPost ? (
                <Link
                  to={`/blog/${nextPost.slug}`}
                  className="group flex flex-col items-end text-right p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <span className="text-xs text-gray-500 mb-1">Next</span>
                  <span className="text-sm font-medium text-gray-900 group-hover:text-blue-700 transition-colors">
                    {nextPost.title}
                  </span>
                </Link>
              ) : (
                <div />
              )}
            </div>

            {/* Back to all */}
            <div className="text-center mt-8">
              <Link
                to="/blog"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                View All Insights
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
